import { contentAiSteps, useAiArticlesProgress } from '../steps/ContentAiSteps';
import { useWizard } from 'react-use-wizard';
import React, { useState } from 'react';
import { useIntl } from 'react-intl';
import { CustomStepper, CustomStepperStep } from '@mi-front/ui';
import { Box, IconButton } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';

export function WizardHeader({
  steps,
  onClose,
}: {
  steps: typeof contentAiSteps;
  onClose?: () => void;
}) {
  const { activeStep, goToStep } = useWizard();
  const aiArticlesProgress = useAiArticlesProgress();
  const { $t } = useIntl();

  // Calculate internal progress based on active step
  const internalProgress = activeStep === 0 ? aiArticlesProgress.progress : undefined;
  const [isExpanded, setIsExpanded] = useState(true);

  // Visual steps include the pre-completed "Crear fragmento" step
  const visualSteps: CustomStepperStep[] = [
    {
      label: $t({
        id: 'contentAi.wizard.createFragmentStep',
        defaultMessage: 'Crear fragmento',
      }),
    },
    ...steps.map((step) => ({
      label: $t(step.message),
    })),
  ];

  // Visual active step is wizard activeStep + 1 (to account for the pre-completed step)
  const visualActiveStep = activeStep + 1;

  const handleStepClick = (stepIndex: number) => {
    // Step 0 is always completed and not clickable
    if (stepIndex === 0) return;

    // Only allow clicking on completed steps (adjust for visual offset)
    const wizardStepIndex = stepIndex - 1;
    if (wizardStepIndex < activeStep) {
      goToStep(wizardStepIndex);
    }
  };

  // Dynamic titles based on active step
  const getWizardTitle = (stepIndex: number): string => {
    const stepTitles: Record<number, string> = {
      0: $t(
        {
          id: 'contentAi.wizard.almostThereTitle',
          defaultMessage: '¡Ya casi estamos! Solo {count} pasos más.',
        },
        { count: steps.length },
      ),
      1: $t({
        id: 'contentAi.wizard.editArticleTitle',
        defaultMessage: 'Edita tu artículo para publicarlo.',
      }),
      2: $t({
        id: 'contentAi.wizard.adaptContentTitle',
        defaultMessage: 'Adapta el contenido y publícalo en las redes.',
      }),
    };
    return stepTitles[stepIndex] ?? stepTitles[0];
  };

  return (
    <Box
      sx={{
        backgroundColor: '#ffffff',
        borderRadius: '8px',
        padding: '32px 24px',
        display: 'flex',
        flexDirection: 'column',
        gap: '32px',
      }}
    >
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'flex-start',
        }}
      >
        <Box sx={{ flex: 1 }}>
          <Box
            sx={{
              padding: '0',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
            }}
          >
            <Box
              component="div"
              sx={{
                color: '#0E0F11',
                fontSize: 24,
                fontFamily: 'Inter',
                fontWeight: 600,
                lineHeight: '32px',
                letterSpacing: '-0.408px',
              }}
            >
              {getWizardTitle(activeStep)}
            </Box>
            <IconButton
              onClick={() => setIsExpanded(!isExpanded)}
              sx={{
                width: 32,
                height: 32,
                padding: '6px 8px',
                border: '1px solid #D0D4D9',
                borderRadius: '6px',
                '&:hover': {
                  backgroundColor: '#F7F8F9',
                },
              }}
            >
              {isExpanded ? (
                <ExpandLessIcon sx={{ fontSize: 20, color: '#0E0F11' }} />
              ) : (
                <ExpandMoreIcon sx={{ fontSize: 20, color: '#0E0F11' }} />
              )}
            </IconButton>
          </Box>
          {isExpanded && (
            <CustomStepper
              steps={visualSteps}
              activeStep={visualActiveStep}
              onStepClick={handleStepClick}
              internalProgress={internalProgress}
            />
          )}
        </Box>
        {onClose && (
          <IconButton
            onClick={onClose}
            sx={{
              width: 32,
              height: 32,
              padding: '6px 8px',
              border: '1px solid #D0D4D9',
              borderRadius: '6px',
              '&:hover': {
                backgroundColor: '#F7F8F9',
              },
            }}
          >
            <CloseIcon sx={{ fontSize: 20, color: '#0E0F11' }} />
          </IconButton>
        )}
      </Box>
    </Box>
  );
}
